/**
 * Message Sync Hook
 *
 * Syncs persisted messages into useChat's setMessages when a conversation
 * loads or changes.
 *
 * @module hooks/chat/useMessageSync
 */

import { useEffect, useRef } from 'react';
import type { UIMessage } from '@ai-sdk/react';

export type MessageSyncEvent = {
  conversationId: string | null;
  previousConversationId: string | null;
  initialMessageCount: number;
  currentMessageCount: number;
  setMessagesCalled: boolean;
  reason:
    | 'conversation-cleared'
    | 'conversation-switched'
    | 'messages-seeded'
    | 'messages-skipped-live';
};

export function useMessageSync<TMessage extends UIMessage = UIMessage>(opts: {
  /** Current conversation ID (null when no conversation selected) */
  conversationId: string | null;
  /** Messages loaded from the persistence layer, converted to UIMessage format */
  initialMessages: TMessage[];
  /** setMessages from useChat */
  setMessages: (messages: TMessage[]) => void;
  /**
   * Number of live messages currently in useChat state. Used to avoid
   * clobbering an in-progress conversation with a late/stale persisted
   * refetch — see the seed guard below.
   */
  currentMessageCount: number;
  /** Optional telemetry hook for consumers debugging sync/update loops. */
  onSyncEvent?: (event: MessageSyncEvent) => void;
}): void {
  const { conversationId, initialMessages, setMessages, currentMessageCount, onSyncEvent } = opts;

  const lastConversationIdRef = useRef<string | null>(null);
  const hasLoadedMessagesRef = useRef(false);

  // Mirror the live message count into a ref so the seed effect can read it
  // without listing it as a dependency (which would re-run the effect on every
  // streamed token).
  const messageCountRef = useRef(currentMessageCount);
  messageCountRef.current = currentMessageCount;

  useEffect(() => {
    if (!conversationId) {
      onSyncEvent?.({
        conversationId,
        previousConversationId: lastConversationIdRef.current,
        initialMessageCount: initialMessages.length,
        currentMessageCount: messageCountRef.current,
        setMessagesCalled: false,
        reason: 'conversation-cleared',
      });
      lastConversationIdRef.current = null;
      hasLoadedMessagesRef.current = false;
      return;
    }

    const isNewConversation = lastConversationIdRef.current !== conversationId;
    const messagesJustLoaded = !hasLoadedMessagesRef.current && initialMessages.length > 0;

    if (isNewConversation) {
      const previousConversationId = lastConversationIdRef.current;
      lastConversationIdRef.current = conversationId;
      hasLoadedMessagesRef.current = false;
      // Switching conversations: reset to the new conversation's persisted
      // history (or clear when it has none loaded yet).
      setMessages(initialMessages);
      onSyncEvent?.({
        conversationId,
        previousConversationId,
        initialMessageCount: initialMessages.length,
        currentMessageCount: messageCountRef.current,
        setMessagesCalled: true,
        reason: 'conversation-switched',
      });
      if (initialMessages.length > 0) hasLoadedMessagesRef.current = true;
      return;
    }

    if (messagesJustLoaded) {
      // Same conversation, persisted history arrived for the first time. Only
      // seed local state when it's empty. If the user is already chatting here
      // (local already holds streamed messages), a late or stale refetch must
      // NOT overwrite them — that blanked the just-streamed reply until a
      // refresh. A merge-by-id is unsafe too: the persisted user message uses
      // the clientMessageId we mint in beforeSend, which differs from ai-sdk's
      // local message id, so merging would duplicate the user turn.
      if (messageCountRef.current === 0) {
        setMessages(initialMessages);
        onSyncEvent?.({
          conversationId,
          previousConversationId: lastConversationIdRef.current,
          initialMessageCount: initialMessages.length,
          currentMessageCount: messageCountRef.current,
          setMessagesCalled: true,
          reason: 'messages-seeded',
        });
      } else {
        onSyncEvent?.({
          conversationId,
          previousConversationId: lastConversationIdRef.current,
          initialMessageCount: initialMessages.length,
          currentMessageCount: messageCountRef.current,
          setMessagesCalled: false,
          reason: 'messages-skipped-live',
        });
      }
      hasLoadedMessagesRef.current = true;
    }
  }, [conversationId, initialMessages, setMessages, onSyncEvent]);
}
